import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import { GlobalFreeze, UpsertGlobalFreezeDto } from '../../types/freeze.types';
import { useSTKContext } from '@/context/context';

export const useGlobalFreezes = (enabled: boolean) => {
  const { apiClient } = useSTKContext();
  return useQuery<AxiosResponse<GlobalFreeze[]>>({
    queryKey: ['global-freezes', 'current-and-future'],
    queryFn: () => apiClient.get<GlobalFreeze[]>('/global-freezes?filterType=CURRENT_AND_FUTURE'),
    enabled,
  });
};

export const useActiveGlobalFreezes = (enabled: boolean) => {
  const { apiClient } = useSTKContext();
  return useQuery<AxiosResponse<GlobalFreeze[]>>({
    queryKey: ['global-freezes', 'active'],
    queryFn: () => apiClient.get<GlobalFreeze[]>('/global-freezes?filterType=ACTIVE'),
    enabled,
  });
};

export const useExistsActiveGlobalFreezes = () => {
  const { apiClient } = useSTKContext();
  return useQuery<boolean>({
    queryKey: ['global-freezes-active'],
    queryFn: async () => {
      const response = await apiClient.get<GlobalFreeze[]>('/global-freezes?filterType=ACTIVE');
      const freezeExists = response.data && response.data?.length > 0;
      return freezeExists;
    },
    enabled: true,
  });
};

export const useExistsActiveFreezes = () => {
  const { apiClient } = useSTKContext();
  return useQuery<boolean>({
    queryKey: ['freezes-actives'],
    queryFn: async () => {
      const response = await apiClient.get<boolean>('/global-freezes/exists?filterType=ACTIVE');
      return response.data;
    },
    enabled: true,
  });
};

export const useGlobalFreeze = (enabled: boolean, id: string) => {
  const { apiClient } = useSTKContext();
  return useQuery<AxiosResponse<GlobalFreeze>>({
    queryKey: ['global-freezes', id],
    queryFn: () => apiClient.get<GlobalFreeze>(`/global-freezes/${id}`),
    enabled,
  });
};

export const useUpsertGlobalFreeze = (freezeId?: string) => {
  const { apiClient } = useSTKContext();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (freeze: UpsertGlobalFreezeDto) =>
      freezeId ? apiClient.put(`/global-freezes/${freezeId}`, freeze) : apiClient.post(`/global-freezes`, freeze),
    onSuccess: () => {
      queryClient.invalidateQueries(['global-freezes']);
    },
  });
};

export const useDeleteGlobalFreeze = () => {
  const { apiClient } = useSTKContext();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (freezeId?: string) => apiClient.delete(`/global-freezes/${freezeId}`),
    onSuccess: () => {
      queryClient.invalidateQueries(['global-freezes']);
    },
  });
};
